import PropTypes from 'prop-types';
import React, { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import { SimpleFlexbox } from 'ctm-components';
import { resultsConfigSelector, sortedAndFilteredProductsSelector } from 'ctm-selectors';
import { getMultiLevelSortCriteria } from 'ctm-util-functions/lib/multi_sorting_config';
import { DisplayToggle } from '../display_toggle/display_toggle';
import { FrequencyToggle } from '../frequency_toggle/frequency_toggle';
import { WelcomeMessage } from '../welcome_message/welcome_message';

/**
 * _ResultsToggle includes Results Welcome message, Display Mode and Frequency Toggle Buttons
 */
class _ResultsToggle extends PureComponent {

    /**
     * Check if results has quote and also not in compare mode
     * @return {Boolean} - True if results has quotes and not in compare mode, otherwise false
     */
    hasQuotesAndNoCompareMode = () => {
        const { compareModeActive, hasQuotes } = this.props;
        return !compareModeActive && hasQuotes;
    };

    /**
     * Render WelcomeMessage component at the top of the results page for mobile breakpoint
     * @return {Node} WelcomeMessage component with Flexbox wrapper
     */
    getTopWelcomeMessage = () => {
        return this.hasQuotesAndNoCompareMode() ? (
            <SimpleFlexbox padding="all" display="flex" xs={12} sm={0} md={0} lg={0} xl={0} xxl={0} itemsCenter="xs" contentCenter="xs">
                <WelcomeMessage />
            </SimpleFlexbox>
        ) : null;
    };

    /**
     * Render WelcomeMessage component normally for other breakpoints
     * @return {Node} WelcomeMessage component
     */
    getWelcomeMessage = () => {
        return this.hasQuotesAndNoCompareMode() ? <WelcomeMessage /> : null;
    };

    /**
     * Render DisplayToggle component
     * @return {Node} DisplayToggle returns DisplayToggle component
     */
    getDisplayToggle = () => {
        const { displayMode, previousDisplayMode, setResultsConfigValue } = this.props;
        return this.hasQuotesAndNoCompareMode() ? (
            <DisplayToggle
                displayMode={displayMode}
                previousDisplayMode={previousDisplayMode}
                UIComponent="btn_primary_transparent_medium_semi_rounded"
                UIComponentActive="btn_feature_1_medium_semi_rounded"
                containerClass="button_display_mode_container"
                setResultsConfigValue={setResultsConfigValue}
            />
        ) : null;
    };

    /**
     * Render FrequencyToggle component
     * @return {Node} FrequencyToggle returns FrequencyToggle component
     */
    getFrequencyToggle = () => {
        const { hasQuotes, frequency, sortResults, sortArrayMultiLevel, setFrequencyFilter } = this.props;
        return hasQuotes ? (
            <FrequencyToggle
                frequency={frequency}
                UIComponent="btn_primary_transparent_medium_semi_rounded"
                UIComponentActive="btn_feature_1_medium_semi_rounded"
                containerClass="button_display_mode_container"
                sortResults={sortResults}
                sortArrayMultiLevel={sortArrayMultiLevel}
                setFrequencyFilter={setFrequencyFilter}
            />
        ) : null;
    };

    /**
     * Render the Results Toggle component including the welcome message, Display Mode and Frequency Toggle Buttons
     * @returns {Node} ResultsToggle - the Results Toggle component
     */
    render = () => {
        return (
            <>
                {this.getTopWelcomeMessage()}
                <SimpleFlexbox margin="top" padding="none" display="flex" xs={12} md={9} lg={9} xl={10} xxl={12} itemsCenter="sm" contentBetween="xs">
                    <SimpleFlexbox gapless="xs" xs={12} sm={5}>
                        {this.getDisplayToggle()}
                    </SimpleFlexbox>
                    <SimpleFlexbox gapless="xs" display="flex" xs={0} sm={4}>
                        {this.getWelcomeMessage()}
                    </SimpleFlexbox>
                    <SimpleFlexbox gapless="xs" xs={12} sm={3} display="flex" contentEnd="xs" variant="max_width_100">
                        {this.getFrequencyToggle()}
                    </SimpleFlexbox>
                </SimpleFlexbox>
            </>
        );
    };

}

_ResultsToggle.propTypes = {
    displayMode: PropTypes.string,
    compareModeActive: PropTypes.bool,
    frequency: PropTypes.string,
    previousDisplayMode: PropTypes.string,
    setResultsConfigValue: PropTypes.func.isRequired,
    setFrequencyFilter: PropTypes.func.isRequired,
    sortResults: PropTypes.func.isRequired,
    sortArrayMultiLevel: PropTypes.func.isRequired,
    hasQuotes: PropTypes.bool.isRequired
};

/**
 * Map dispatch function to component props
 * @param {Function} dispatch - store's dispatch function
 * @returns {Object} props that contain sortResults, setFrequencyFilter and setResultsConfigValue function
 */
const mapDispatchToProps = (dispatch) => ({
    sortResults: (frequency) => {
        dispatch({
            type: 'SORT_RESULTS',
            sortBy: `productMeta.price.${frequency}Premium`,
            sortDir: 'asc',
            sortType: 'numeric'
        });
    },
    sortArrayMultiLevel: (frequency) => {
        dispatch({
            type: 'SORT_ARRAY_MULTI_LEVEL',
            sortCriteria: getMultiLevelSortCriteria(frequency)
        });
    },
    setFrequencyFilter: (frequency) => {
        dispatch({
            type: 'SET_FILTER_VALUE',
            filter: 'frequency',
            filterValue: frequency
        });
    },
    setResultsConfigValue: (value, key) => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key,
            value
        });
    }
});

const mapStateToProps = (state) => {
    return {
        displayMode: resultsConfigSelector(state).getIn(['displayMode']),
        compareModeActive: resultsConfigSelector(state).getIn(['compareModeActive']),
        previousDisplayMode: resultsConfigSelector(state).getIn(['previousDisplayMode']),
        frequency: resultsConfigSelector(state).getIn(['filters', 'frequency']),
        hasQuotes: !sortedAndFilteredProductsSelector(state).isEmpty()
    };
};

const ResultsToggle = connect(
    mapStateToProps,
    mapDispatchToProps
)(_ResultsToggle);

export { _ResultsToggle, ResultsToggle };
